// Split search — one box, two worlds.
//
// The end state of docs/2d-3d-search.md, and the thing that makes it a
// 2D/3D feature rather than two search boxes. What it deliberately does
// NOT do is claim a 2D hit and a 3D hit are the same object: there is no
// link table, so the honest claim is "both matched P101", which is what
// the agreement column says.
//
// Click-across works the same way: selecting anything derives a query
// from it (helpers/selectionBus) and re-runs the ordinary search. Typing
// and clicking enter the same pipe, so there is one code path to be
// wrong, not two.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Empty, Input, Select, Space, Spin, Tag, Tooltip, message,
} from "antd";
import {
  SearchOutlined, ReloadOutlined, ArrowsAltOutlined, ShrinkOutlined, WarningOutlined,
} from "@ant-design/icons";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import Canvas2D from "components/SplitSearch/Canvas2D";
import { searchAnchors, listAnchorSheets, reindexModel, queueAllSheets, elementAnchor, locateGlance } from "api/anchors/anchorApi";
import FloatingPanel from "components/SplitSearch/FloatingPanel";
import ModelPane from "components/SplitSearch/ModelPane";
import { select as busSelect, queryFor } from "helpers/selectionBus";
import RaiseIssueModal from "components/SplitSearch/RaiseIssueModal";
import { linkTargetOf, targetFromLocate } from "./linkTarget";
// THE SAME PANEL AS CONSTRUCTION TRACKING, not a copy of it. See
// panelSelection.js for why it is shared rather than forked, and for how
// the write path is kept out by omission.
import SelectionDetails from "pages/Tracking/SelectionDetails";
import { panelProps } from "./panelSelection";
import PlanFallback from "components/SplitSearch/PlanFallback";
import { matchOption } from "components/SearchSelect/searchSelect";

// A pane header over its content.
//
// DEFINED AT MODULE SCOPE, and it matters. This lived inside
// SplitSearchPage, which made it a NEW component type on every render -
// so React unmounted and remounted everything inside it, every time.
// The 2D canvas was therefore being destroyed and rebuilt constantly:
// re-fetching the sheet image and throwing away zoom and pan. It also
// reset the canvas's refs, which turned a single upward "this is the
// sheet I am showing" report into an infinite loop the moment one was
// added - React error 185, blank page.
// Expand / restore, in each pane header.
function ExpandButton({ on, onToggle }) {
  return (
    <Tooltip title={on ? "Back to the split" : "Fill the window with this pane"}>
      <Button
        size="small"
        type="text"
        icon={on ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
        onClick={onToggle}
      />
    </Tooltip>
  );
}

function Pane({ title, count, children, action }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0 }}>
      <div style={{
        padding: "6px 10px", background: "#fafafa", borderBottom: "1px solid #eee",
        fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
      }}>
        <span>{title}</span>
        <span style={{ marginLeft: "auto", color: "#888", fontWeight: 400 }}>{count}</span>
        {action}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{children}</div>
    </div>
  );
}

const PANE_MIN = 260;

// A 2D hit in full, for the floating panel over an expanded sheet - and the
// way through the hits, since the list under the sheet is hidden then.
function SheetHitProperties({ hit, hits, onPick, onRaise }) {
  const onSheets = hits.filter((h) => h.docId != null && h.page != null);
  const at = hit ? onSheets.findIndex((h) => String(h._id) === String(hit._id)) : -1;
  const go = (d) => {
    if (!onSheets.length) return;
    const n = at < 0 ? (d > 0 ? 0 : onSheets.length - 1) : (at + d + onSheets.length) % onSheets.length;
    onPick(onSheets[n]);
  };
  const row = (k, v) => (v || v === 0) ? (
    <tr key={k} style={{ borderBottom: "1px solid #f3f3f3" }}>
      <td style={{ padding: "3px 6px 3px 10px", color: "#666", width: "38%", verticalAlign: "top" }}>{k}</td>
      <td style={{ padding: "3px 10px 3px 4px", wordBreak: "break-word" }}>{v}</td>
    </tr>
  ) : null;
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderBottom: "1px solid #f0f0f0" }}>
        <Button size="small" onClick={() => go(-1)} disabled={!onSheets.length}>Previous</Button>
        <Button size="small" onClick={() => go(1)} disabled={!onSheets.length}>Next</Button>
        <span style={{ color: "#888", marginLeft: "auto" }}>
          {onSheets.length ? `${at < 0 ? "-" : at + 1} of ${onSheets.length} hits` : "no hits on the sheets"}
        </span>
      </div>
      {!hit ? (
        <div style={{ padding: 12, color: "#888" }}>Click a highlight on the sheet, or step through the hits.</div>
      ) : (
        <>
          <div style={{ padding: "8px 10px", fontWeight: 600, fontSize: 12.5 }}>{hit.text || "(no text)"}</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {row("Sheet", [hit.sheetNumber, hit.sheetName].filter(Boolean).join(" ") || null)}
              {row("Page", hit.page != null ? hit.page + 1 : null)}
              {row("Grid", hit.drawingGrid && hit.drawingGrid.col && hit.drawingGrid.row ? `${hit.drawingGrid.col}-${hit.drawingGrid.row}` : null)}
              {row("Cell", hit.cell)}
              {row("Level", hit.level)}
              {row("Kind", hit.kind)}
              {row("Matched", hit.matched === "target" ? "linked" : hit.matched)}
              {row("Tags", (hit.tags || []).join(" · ") || null)}
              {row("Revision", hit.revisionNumber)}
              {row("In the model too", hit.agreed ? "yes - the same token came back from the model" : null)}
            </tbody>
          </table>
          <div style={{ padding: "8px 10px" }}>
            <Button size="small" icon={<WarningOutlined />} onClick={() => onRaise(hit)}>Raise issue</Button>
          </div>
        </>
      )}
    </div>
  );
}

// sheetSearchView (owner, 2026-10-14): on the project team's side the tab
// needs the key; the server says so on the sheet list (403 naming it) and
// the page then shows this in place of the search. Out of the page body so
// the hooks lint has one branch fewer to walk.
const SEARCH_KEY = "sheetSearchView";
const deniedBy = (e) => !!(e && e.status === 403 && e.missingPermission === SEARCH_KEY);
function SearchDenied() {
  return (
    <div style={{ padding: 24 }}>
      <Alert type="info" showIcon
             message="Find in Sheets & Model needs the Find in sheets view permission on this project. Ask a company admin to grant it." />
    </div>
  );
}

export default function SplitSearchPage() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useLastProject(projects);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState("");
  const [res, setRes] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [indexing, setIndexing] = useState(false);
  // Split position as a fraction of the width, dragged by the divider.
  const [split, setSplit] = useState(0.5);
  // "Search inside this sheet": which sheet the 2D pane is showing, and
  // whether the query should be confined to it.
  // One pane, all the width. A drawing is read at size, and the split
  // was always a compromise between two things you rarely need at once.
  const [maximised, setMaximised] = useState(null); // null | '2d' | '3d'
  const [activeSheet, setActiveSheet] = useState(null);
  const [scopeToSheet, setScopeToSheet] = useState(false);
  const shellRef = useRef(null);
  const dragging = useRef(false);
  // Raising an issue from a result: which hit it started from, and which
  // pane that hit was in, so the modal can default to the right world.
  const [raising, setRaising] = useState(null); // null | { hit, world }
  const sheetImgRef = useRef(null);
  // The glance link: the sheet it names and the box on it, until the
  // reader goes somewhere else.
  const [link, setLink] = useState(null);
  const [linkFocus, setLinkFocus] = useState("");
  // 'plan' | 'sheet' once the reader chooses; until then the plan shows
  // only when the link's room has no sheet this pane can open.
  const [planChoice, setPlanChoice] = useState(null);
  // The 2D pane's floating panel, when that pane fills the window. Closed
  // by its X; a new pick opens it again.
  const [hitPanel2d, setHitPanel2d] = useState(true);
  useEffect(() => { setHitPanel2d(true); }, [selected]);

  useEffect(() => {
    // ?include= keeps a linked project in the list for a reader who is not
    // on its roster (a company admin): without it the picker fell back to
    // the first project while the results came from the linked one, and
    // the next search went to the wrong project (root-04, 2026-09-19).
    let linked = "";
    try { linked = new URLSearchParams(window.location.search).get("projectId") || ""; } catch (e) { /* none */ }
    const include = /^[0-9a-f]{24}$/i.test(linked) ? `?include=${linked}` : "";
    fetch(`${url}/projects/picker${include}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : rows?.projects || [];
        setProjects(list);
        setProjectId((cur) => (cur && list.some((p) => String(p._id) === String(cur)) ? cur : list[0]?._id || ""));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSheets = useCallback(() => {
    if (!projectId) return;
    listAnchorSheets(projectId)
      .then((r) => { setDenied(false); setSheets(r.sheets || []); })
      .catch((e) => { setDenied(deniedBy(e)); setSheets([]); });
  }, [projectId]);

  useEffect(() => { loadSheets(); setRes(null); setSelected(null); setQ(""); setDraft(""); }, [projectId, loadSheets]);

  // The one search. Everything - the box, a click in either pane -
  // arrives here.
  // The scope is passed IN rather than read from state.
  //
  // The first version re-ran the search from a useEffect keyed on the
  // scope and the active sheet. That looped: the canvas reports its
  // sheet upward, the page sets state, the canvas re-renders and reports
  // again - React error 185, "maximum update depth exceeded", and a
  // blank page. The two events that should re-ask a question are ticking
  // the box and changing sheet, and both are events, so they re-ask
  // directly and nothing watches anything.
  // `target` (the glance link's room/level/element) belongs to the link's
  // own question only; anything typed afterwards is an ordinary search.
  const runScoped = useCallback((text, scope, target) => {
    const query = String(text || "").trim();
    setQ(query);
    if (!projectId || !query) { setRes(null); return Promise.resolve(null); }
    setBusy(true);
    return searchAnchors(projectId, query, 100, scope || null, target || null)
      .then((r) => { setRes(r); return r; })
      .catch((e) => { message.error(e.message || "Search failed"); return null; })
      .finally(() => setBusy(false));
  }, [projectId]);

  const scopeRef = useRef({ on: false, sheet: null });
  scopeRef.current = { on: scopeToSheet, sheet: activeSheet };
  const run = useCallback((text) => {
    const { on, sheet } = scopeRef.current;
    runScoped(text, on && sheet ? sheet : null);
  }, [runScoped]);

  // ?q= (the project overview's "Find in model and sheets"): ask it once,
  // as soon as the project is known. The URL's ?projectId= already picked
  // the project through useLastProject.
  const pickInModelRef = useRef(() => {});
  const askedFromUrl = useRef(false);
  useEffect(() => {
    if (askedFromUrl.current || !projectId) return;
    const l = linkTargetOf(window.location.search);
    const fromUrl = l.q;
    const element = (l.target && l.target.uniqueId) || "";
    // ?uniqueId= alone comes from a technical view row that has been linked
    // to the model: open on that element, the same as clicking it in 3D.
    if (element && !fromUrl) {
      askedFromUrl.current = true;
      pickInModelRef.current(element);
      return;
    }
    if (!fromUrl && !l.glance) return;
    askedFromUrl.current = true;
    // glance=room:<id> is resolved first (level, sheet, box on the sheet);
    // a building-data answer that fails still leaves q to search on.
    const resolved = l.glance
      ? locateGlance(projectId, l.glance).then(targetFromLocate).catch(() => null)
      : Promise.resolve(null);
    resolved.then((loc) => {
      const it = loc ? { ...l, ...loc, q: fromUrl || loc.label } : l;
      if (!it.q) return;
      setDraft(it.q);
      setLink(it.sheet || it.target ? it : null);
      // The room itself, found exactly, is selected and its page opened;
      // failing that the page the link names. Set AFTER the answer lands
      // so the canvas's "new question" reset does not undo it.
      runScoped(it.q, null, it.target).then((r) => {
        const t = r && (r["2d"] || []).find((h) => h.matched === "target" && h.docId != null);
        if (t) setSelected(t);
        else if (it.sheet) setLinkFocus(`${it.sheet.docId}:${it.sheet.page}`);
      });
    });
  }, [projectId, runScoped]);

  // Selecting in one pane re-queries for the other. No link is resolved
  // because none exists.
  const pick = useCallback((anchor, world) => {
    setLinkFocus("");
    setSelected(anchor);
    busSelect({ world, anchor, projectId, query: queryFor(anchor) });
    const derived = queryFor(anchor);
    if (derived && derived !== q) { setDraft(derived); run(derived); }
  }, [projectId, q, run]);

  // A click in the model: find the element's type, select it and ask
  // the search about it, so the sheets say where it is drawn.
  const pickInModel = useCallback(async (uniqueId) => {
    if (!projectId || !uniqueId) return;
    try {
      const r = await elementAnchor(projectId, uniqueId);
      if (r && r.anchor) {
        setSelected({ ...r.anchor, world: "3d" });
        const derived = r.derivedQuery || queryFor(r.anchor);
        if (derived) { setDraft(derived); run(derived); }
      }
    } catch (e) {
      message.info("That element is not in the search index yet");
    }
  }, [projectId, run]);
  // Assigned during render, NOT in an effect: effects run in declaration
  // order, so the ?uniqueId= effect above would otherwise fire while this ref
  // still held the placeholder and the deep link would quietly do nothing.
  pickInModelRef.current = pickInModel;

  // Raising an issue selects the hit first, so the canvas is showing the
  // page the snapshot is about before the modal opens over it.
  const raise = useCallback((h, world) => {
    setSelected(h);
    setRaising({ hit: h, world });
  }, []);

  // Memoised so the identity only changes when the RESULTS do. Built
  // inline, `|| []` minted a fresh empty array on every render of this
  // page, and the panes below treat a new array as new results - which
  // is how a redraw became a reason to recompute the busiest sheet and,
  // before the fix in Canvas2D, to throw away the reader's sheet.
  const hits3d = useMemo(() => (res && res["3d"]) || [], [res]);
  const hits2d = useMemo(() => (res && res["2d"]) || [], [res]);
  const agreed = useMemo(() => (res && res.intersection) || [], [res]);
  // ONE PLACE builds the panel's whole prop set - see panelSelection.js.
  // Nothing is added at the call site, which is what keeps the write path
  // out by construction rather than by everybody remembering.
  const detailProps = useMemo(
    () => panelProps({ projectId, selected: selected && selected.world === "3d" ? selected : null }),
    [projectId, selected],
  );

  // The building-plan fallback for a glance room whose drawing cannot be
  // shown here (a DWG with no rendered page, Apple RT06).
  const linkTarget = link && link.target;
  const planApplies = !!(linkTarget && (linkTarget.level || linkTarget.levelName)
    && (linkTarget.roomId || linkTarget.room || linkTarget.roomName));
  const linkSheetShowable = useMemo(() => {
    if (!link) return false;
    if (hits2d.some((h) => h.matched === "target" && h.docId != null)) return true;
    return !!(link.sheet && sheets.some((x) => String(x.docId) === String(link.sheet.docId)));
  }, [link, hits2d, sheets]);
  const showPlan = planApplies && (planChoice ? planChoice === "plan" : !linkSheetShowable);

  const buildIndex = async () => {
    setIndexing(true);
    try {
      const m = await reindexModel(projectId);
      // Every drawing, queued - not the 25 most recent indexed inside this
      // request, which is all the button ever did. They land over the
      // next minutes; new and revised documents are picked up on their
      // own from now on (utils/sheetIndexCatchUp).
      const s = await queueAllSheets(projectId);
      message.success(
        `Model: ${m.elements} elements. Sheets: ${s.queued} of ${s.documents} documents queued for indexing - they appear here as each one finishes.`,
        8,
      );
      loadSheets();
      if (q) run(q);
    } catch (e) {
      message.error(e.message || "Indexing failed");
    } finally {
      setIndexing(false);
    }
  };

  const onDividerDown = () => { dragging.current = true; };
  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !shellRef.current) return;
      const r = shellRef.current.getBoundingClientRect();
      const f = (e.clientX - r.left) / r.width;
      setSplit(Math.min(0.8, Math.max(0.2, f)));
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  const HitRow = ({ h, world }) => (
    <div
      onClick={() => pick(h, world)}
      style={{
        padding: "6px 8px", borderBottom: "1px solid #f0f0f0", cursor: "pointer",
        background: String(selected?._id) === String(h._id) ? "#fff7f0" : undefined,
      }}
    >
      <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
        {h.agreed && (
          <Tooltip title="This token came back from BOTH worlds. That is agreement, not proof they are the same object.">
            <Tag color="green" style={{ marginRight: 0 }}>agreed</Tag>
          </Tooltip>
        )}
        <Tag color={h.matched === "target" ? "orange" : h.matched === "tag" ? "blue" : h.matched === "alias" ? "purple" : "default"}
             style={{ marginRight: 0 }}>
          {h.matched === "target" ? "linked" : h.matched}
        </Tag>
        <span style={{ fontSize: 12, color: "#888" }}>
          {world === "3d"
            ? `${h.category || h.kind || "element"}${h.count ? ` · ${h.count.toLocaleString()} element${h.count === 1 ? "" : "s"}` : ""}`
            : h.docId == null
              ? `${h.kind === "room" ? "room" : "item"} · ${h.glance && h.glance.srcName ? h.glance.srcName : "project overview"} (drawing can't be shown yet)`
              : `${h.sheetNumber || "sheet"}${h.page ? ` p${h.page + 1}` : ""} ${h.cell || ""}`}
          {(h.drawingGrid && h.drawingGrid.col && h.drawingGrid.row)
            ? ` · ${h.drawingGrid.col}-${h.drawingGrid.row}` : ""}
          {h.level ? ` · ${h.level}` : ""}
        </span>
        <Tooltip title="Raise an issue about this result">
          <Button
            size="small" type="text" style={{ marginLeft: "auto" }}
            icon={<WarningOutlined />}
            onClick={(e) => { e.stopPropagation(); raise(h, world); }}
          />
        </Tooltip>
      </div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{h.text || "(no text)"}</div>
      {!!(h.tags || []).length && (
        <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{h.tags.join(" · ")}</div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100 * var(--vh) - var(--app-bars-h, 60px))" }}>
      <div style={{ padding: "10px 16px 0" }}>
        <CoordinationBreadcrumbs current="Find in Sheets &amp; Model" />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 16px", flexWrap: "wrap" }}>
        <Select filterOption={matchOption}
          showSearch optionFilterProp="label" style={{ width: 220 }}
          value={projectId || undefined} placeholder="Project"
          onChange={setProjectId}
          options={projects.map((p) => ({ value: p._id, label: p.title }))}
        />
        {!denied && (<>
        <Input
          style={{ width: 380 }}
          allowClear
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          // The box gives up focus once the question is asked: the model's
          // keys (F, H, G, M, C, I, S) are ignored while a text box has it,
          // so every shortcut was dead until the reader clicked elsewhere.
          onPressEnter={(e) => { run(draft); if (e && e.target && e.target.blur) e.target.blur(); }}
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="P-101, CT-3, grid C-4, conduit level 2…"
        />
        <Button type="primary" onClick={() => run(draft)} loading={busy}>Search</Button>
        <Tooltip title="Rebuild the 3D anchors from the model export and index up to 25 Doc Control documents">
          <Button icon={<ReloadOutlined />} loading={indexing} onClick={buildIndex}>Index</Button>
        </Tooltip>
        <Tooltip title={res ? "Raise an issue about these results" : "Search first"}>
          <Button
            icon={<WarningOutlined />}
            disabled={!res || (!hits2d.length && !hits3d.length)}
            onClick={() => raise(selected || hits2d[0] || hits3d[0], selected ? (selected.world || "2d") : (hits2d.length ? "2d" : "3d"))}
          >
            Raise issue
          </Button>
        </Tooltip>

        {res && (
          <Space size={4} wrap style={{ marginLeft: 8 }}>
            {!!(res.tags || []).length && <span style={{ fontSize: 12, color: "#888" }}>tags: {res.tags.join(" ")}</span>}
            {(res.where && (res.where.level || res.where.grid)) && (
              <Tooltip title="What the query was read as asking about, rather than asking for. A place narrows the search; on its own it lists what is there.">
                <Tag color="geekblue">
                  {res.where.grid ? `grid ${res.where.grid.col}-${res.where.grid.row}` : ""}
                  {res.where.grid && res.where.level ? " · " : ""}
                  {res.where.level || ""}
                </Tag>
              </Tooltip>
            )}
            {!!(res.aliasedTo || []).length && (
              <Tooltip title="Reached through this project's vocabulary, not typed">
                <Tag color="purple">via {res.aliasedTo.join(" ")}</Tag>
              </Tooltip>
            )}
            {agreed.length > 0 && (
              <Tooltip title="Canonical tokens that BOTH worlds returned">
                <Tag color="green">both matched: {agreed.join(" ")}</Tag>
              </Tooltip>
            )}
            {q && !agreed.length && (
              <Tag>no overlap between the worlds</Tag>
            )}
            {res.grid && res.grid.version > 0 && (
              <Tooltip title={`Sheets and model are matched on the building grid, version ${res.grid.version}${res.grid.source ? `, from ${res.grid.source.replace(/^align: /, "an alignment of ")}` : ""}.`}>
                <Tag>matched on grid v{res.grid.version}</Tag>
              </Tooltip>
            )}
            {res.grid && res.grid.lastMove && (res.grid.lastMove.maxFt == null || res.grid.lastMove.maxFt > 2) && res.grid.lastMove.sheetsFlagged > 0 && (
              <Tooltip title={`Grid v${res.grid.lastMove.toVersion} moved lines by up to ${res.grid.lastMove.maxFt != null ? `${res.grid.lastMove.maxFt} ft` : "a whole axis"} from v${res.grid.lastMove.fromVersion}. The sheets marked "grid moved" have matches on those lines.`}>
                <Tag color="gold">grid moved - {res.grid.lastMove.sheetsFlagged} sheet{res.grid.lastMove.sheetsFlagged === 1 ? "" : "s"} to check</Tag>
              </Tooltip>
            )}
            {res.target && (res.target.roomId || res.target.itemKey || res.target.room || res.target.roomName) && !res.target.found2d && (
              <Tooltip title="This room or item from the project overview is not in this project's search index yet, so these are text matches only">
                <Tag color="gold">not indexed yet - text matches</Tag>
              </Tooltip>
            )}
            {link && link.sheet && sheets.length > 0
              && !sheets.some((s) => String(s.docId) === String(link.sheet.docId)) && (
              <Tooltip title="The drawing this room was read from has no page this pane can show yet">
                <Tag color="gold">its drawing can't be shown here yet</Tag>
              </Tooltip>
            )}
          </Space>
        )}
        </>)}
      </div>

      {denied ? <SearchDenied /> : (
      <div ref={shellRef} style={{ flex: 1, minHeight: 0, display: "flex", borderTop: "1px solid #eee" }}>
        <div style={{
          width: maximised === "3d" ? "100%" : `${split * 100}%`,
          minWidth: maximised === "3d" ? 0 : PANE_MIN,
          display: maximised === "2d" ? "none" : "flex",
          flexDirection: "column",
        }}>
          <Pane
            title="3D — model elements"
            count={busy ? "…" : `${hits3d.length}${res && res.elements3d ? ` types · ${res.elements3d.toLocaleString()} elements` : ""}`}
            action={<ExpandButton on={maximised === "3d"} onToggle={() => setMaximised((m) => (m === "3d" ? null : "3d"))} />}
          >
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <div style={{ flex: maximised === "3d" ? "1 1 100%" : "1 1 55%", minHeight: 220, borderBottom: "1px solid #eee" }}>
                <ModelPane
                  key={projectId || "none"}
                  projectId={projectId}
                  hits={hits3d}
                  selected={selected && selected.world === "3d" ? selected : null}
                  onPickElement={pickInModel}
                  modelInfo={res ? res.model : null}
                  // Expanded, the list below is gone and the floating panel
                  // is the only place left for the tracking summary.
                  panelExtra={maximised === "3d" && detailProps ? (
                    <div style={{ borderTop: "1px solid #eee" }}><SelectionDetails {...detailProps} /></div>
                  ) : null}
                />
              </div>
              <div style={{ flex: "1 1 45%", overflow: "auto", display: maximised === "3d" ? "none" : undefined }}>
                {/* Everything the model and the tracking board know about
                    what is selected. Above the hit list, because the hits
                    are how you got here and this is the answer. */}
                {detailProps && (
                  <div style={{ borderBottom: "1px solid #eee" }}>
                    <SelectionDetails {...detailProps} />
                  </div>
                )}
                {busy && <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>}
                {!busy && !hits3d.length && (
                  <div style={{ padding: 24 }}>
                    <Empty description={q ? "Nothing in the model matched" : "Search to see model elements"} />
                  </div>
                )}
                {!busy && hits3d.map((h) => <HitRow key={h._id} h={h} world="3d" />)}
              </div>
            </div>
          </Pane>
        </div>

        <div
          onMouseDown={onDividerDown}
          hidden={!!maximised}
          style={{ width: 6, cursor: "col-resize", background: "#eee", flex: "0 0 6px" }}
          title="Drag to resize"
        />

        <div style={{
          flex: 1,
          minWidth: maximised === "2d" ? 0 : PANE_MIN,
          display: maximised === "3d" ? "none" : "flex",
          flexDirection: "column",
        }}>
          <Pane
            title="2D — sheets"
            count={busy ? "…" : `${hits2d.length}`}
            action={<ExpandButton on={maximised === "2d"} onToggle={() => setMaximised((m) => (m === "2d" ? null : "2d"))} />}
          >
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {planApplies && (
                <div style={{ padding: "4px 8px", borderBottom: "1px solid #eee", display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                  <span style={{ color: "#888" }}>Show</span>
                  <Button size="small" type={showPlan ? "primary" : "default"} onClick={() => setPlanChoice("plan")}>Building plan</Button>
                  <Button size="small" type={showPlan ? "default" : "primary"} onClick={() => setPlanChoice("sheet")}>Sheets</Button>
                </div>
              )}
              <div style={{ flex: maximised === "2d" ? "1 1 100%" : "1 1 55%", minHeight: 220, borderBottom: "1px solid #eee" }}>
                {showPlan ? <PlanFallback projectId={projectId} target={linkTarget} /> : (
                <Canvas2D
                  onActiveSheet={(sheet) => {
                    setActiveSheet(sheet);
                    // Changing sheet while confined re-asks about the new one.
                    if (scopeToSheet && q) runScoped(q, sheet);
                  }}
                  scopeOn={scopeToSheet}
                  onScopeChange={(on) => {
                    setScopeToSheet(on);
                    if (q) runScoped(q, on ? activeSheet : null);
                  }}
                  projectId={projectId}
                  imgElRef={sheetImgRef}
                  sheets={sheets}
                  query={q}
                  // Picking a 2D result opens the page it is on. A 3D
                  // selection has no docId and yields "", which leaves
                  // the 2D pane where the reader put it.
                  focusKey={selected && selected.docId != null && selected.page != null
                    ? `${selected.docId}:${selected.page}` : linkFocus}
                  targetBox={link && link.sheet && link.sheetBox
                    ? { docId: link.sheet.docId, page: link.sheet.page, box: link.sheetBox } : null}
                  hits={hits2d}
                  selectedId={selected?._id}
                  onSelect={(h) => pick(h, "2d")}
                  fullscreen={maximised === "2d"}
                  overlay={maximised === "2d" && hitPanel2d ? (
                    <FloatingPanel title="Properties" onClose={() => setHitPanel2d(false)} testId="sheet-properties">
                      <SheetHitProperties
                        hit={selected && selected.world !== "3d" && selected.docId != null ? selected : null}
                        hits={hits2d}
                        // Stepping MOVES the selection and nothing else. Through
                        // pick() each step re-asked the search with the hit's
                        // own text, so the list under the buttons changed on
                        // every press (staging: 95 hits, then 46, then "J J J").
                        onPick={(h) => { setLinkFocus(""); setSelected(h); }}
                        onRaise={(h) => raise(h, "2d")}
                      />
                    </FloatingPanel>
                  ) : null}
                />
                )}
              </div>
              <div style={{ flex: "1 1 45%", overflow: "auto", display: maximised === "2d" ? "none" : undefined }}>
                {!busy && !hits2d.length && (
                  <div style={{ padding: 24 }}>
                    <Empty description={q ? "Nothing on the sheets matched" : "Search to see sheet text"} />
                  </div>
                )}
                {!busy && hits2d.map((h) => <HitRow key={h._id} h={h} world="2d" />)}
              </div>
            </div>
          </Pane>
        </div>
      </div>
      )}

      <RaiseIssueModal
        open={!!raising}
        onClose={() => setRaising(null)}
        projectId={projectId}
        query={q}
        hits2d={hits2d}
        hits3d={hits3d}
        agreed={agreed}
        seed={raising ? raising.hit : null}
        seedWorld={raising ? raising.world : null}
        sheetImgRef={sheetImgRef}
        activeSheet={activeSheet}
        onCreated={({ href }) => {
          message.success({
            content: (
              <span>
                Issue created. <a href={href} target="_blank" rel="noreferrer">Open it</a>
              </span>
            ),
            duration: 6,
          });
        }}
      />
    </div>
  );
}
